// An approval step on a change, decided from the inbox
// apps/smart-catalog/frontend/src/components/orchestrator/ChangeApprovalCard.tsx
//
// The work item is the approver's copy of one workflow step. The card shows
// what they are signing (the problem, what changes, the AI's read) and the
// three answers. A decision goes to the same endpoints as the change page, so
// the workflow records it once and the item resolves itself when it does.

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { changeKeys, useApprovalFlow, useChange } from '../../hooks/useChanges';
import { useExecuteAction } from '../../hooks/useOrchestrator';
import { useQueryClient } from '@tanstack/react-query';
import { CHANGE_STATUS_LABEL, type ApprovalFlowStepV1 } from '../../services/changeService';
import SendForApprovalDialog from '../changes/SendForApprovalDialog';
import { UserRef } from '../changes/UserRef';
import { ChangeSeverityMark } from '../changes/ChangeSeverityMark';

export function dueIn(iso?: string): { label: string; overdue: boolean } | null {
  if (!iso) return null;
  const hours = Math.round((new Date(iso).getTime() - Date.now()) / 3_600_000);
  if (hours < 0) return { label: `overdue ${Math.abs(hours) < 48 ? `${Math.abs(hours)} h` : `${Math.round(-hours / 24)} d`}`, overdue: true };
  return { label: `due in ${hours < 48 ? `${hours} h` : `${Math.round(hours / 24)} d`}`, overdue: false };
}

const leaves = (steps: ApprovalFlowStepV1[]): ApprovalFlowStepV1[] =>
  steps.flatMap((s) => (s.lanes ? s.lanes.flatMap((l) => leaves(l.steps)) : [s]));

/** The fields of a worklist row this card reads. */
interface ChangeWorkItem {
  workItemId: string;
  taskPayload?: Record<string, unknown>;
}

export const ChangeApprovalCard: React.FC<{ item: ChangeWorkItem }> = ({ item }) => {
  const navigate = useNavigate();
  const tp = (item.taskPayload ?? {}) as Record<string, string | undefined>;
  const changeId = tp.changeId;
  const { data, isLoading, refetch } = useChange(changeId);
  const change = data?.change;
  const { data: flows } = useApprovalFlow(changeId, !!change?.workflowRunId, false);
  const flow = flows?.flow;
  // Answers go through the orchestrator, the one approval layer every channel
  // (this queue, and later email, Teams, Slack) uses.
  const queryClient = useQueryClient();
  const execute = useExecuteAction();
  const STEP_ACTION = { approve: 'APPROVE_STEP', 'send-back': 'SEND_BACK_STEP', reject: 'REJECT_STEP' } as const;
  const decide = {
    isPending: execute.isPending,
    isError: execute.isError,
    error: execute.error,
    mutate: (
      { decision, note }: { decision: keyof typeof STEP_ACTION; note?: string; slotId?: string },
      opts?: { onSuccess?: () => void },
    ) =>
      execute.mutate(
        { workItemId: item.workItemId, actionId: STEP_ACTION[decision], input: { note, channel: 'queue' } },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: changeKeys.one(changeId ?? '') });
            opts?.onSuccess?.();
          },
        },
      ),
  };
  const [dialog, setDialog] = useState<'send-back' | 'reject' | null>(null);
  // A copy of the change older than this step: fetch it again once.
  const stale = !!data?.change && !!tp.approvalSlotId && !data.change.approvals?.some((a) => a.slotId === tp.approvalSlotId);
  useEffect(() => {
    if (stale) void refetch();
  }, [stale, refetch]);

  if (isLoading || !change) {
    return (
      <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const slot = change.approvals?.find((a) => a.slotId === tp.approvalSlotId);
  // The work item is open, so the step is, unless the change says otherwise. A
  // change fetched before its flow opened the step has no slot yet: that copy
  // is stale, not a sign that nothing waits (the AI step runs first).
  const open = change.status === 'SUBMITTED' && (slot ? slot.status === 'PENDING' : true);
  const due = dueIn(slot?.dueAt ?? tp.dueAt);
  const ai = flow ? leaves(flow.steps).filter((s) => s.kind === 'agent' && s.note) : [];
  const parts = change.affectedItems?.filter((i) => i.role !== 'PARENT').length ?? 0;
  const lines = data?.redlines.length ?? 0;
  const parents = change.affectedItems?.filter((i) => i.role === 'PARENT').length ?? 0;

  return (
    <Box sx={{ py: 1.5, px: 1, maxWidth: 820 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{slot?.reason ?? tp.stepName ?? 'Approval'}</Typography>
        <ChangeSeverityMark severity={change.severity} label />
        {change.stage && <Chip size="small" label={change.stage} variant="outlined" />}
        {(slot?.escalationLevel ?? 0) > 0 && <Chip size="small" color="error" variant="outlined" label="Escalated" />}
        {due && (
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: due.overdue ? 'error.main' : 'warning.dark' }}>
            {due.label}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />} onClick={() => navigate(`/changes/${change.changeId}`)} sx={{ textTransform: 'none' }}>
          Open change
        </Button>
      </Stack>

      {change.description && (
        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 1, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {change.description}
        </Typography>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 1.25, fontSize: '0.8rem', color: 'text.secondary' }}>
        <span>
          {lines > 0
            ? `${lines} line${lines === 1 ? '' : 's'} change`
            : `${parts} part${parts === 1 ? '' : 's'} change`}
        </span>
        {parents > 0 && (
          <span>
            {parents} assembl{parents === 1 ? 'y' : 'ies'} above get a new revision
          </span>
        )}
        {change.flow && <span>step {Math.min(change.flow.done + 1, change.flow.total)} of {change.flow.total}</span>}
      </Stack>

      {ai.map((s) => (
        <Stack key={s.id} direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.25 }}>
          <AutoAwesomeIcon sx={{ fontSize: 15, color: '#7c3aed' }} />
          <Typography sx={{ fontSize: '0.8rem', color: '#6d28d9' }}>
            {s.name}: {s.note}
          </Typography>
        </Stack>
      ))}

      {open ? (
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ decision: 'approve', slotId: slot?.slotId })}
            sx={{ textTransform: 'none' }}
          >
            Approve
          </Button>
          <Button size="small" variant="outlined" color="inherit" disabled={decide.isPending} onClick={() => setDialog('send-back')} sx={{ textTransform: 'none' }}>
            Send back
          </Button>
          <Button size="small" color="error" disabled={decide.isPending} onClick={() => setDialog('reject')} sx={{ textTransform: 'none' }}>
            Reject
          </Button>
        </Stack>
      ) : (
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
          {slot?.status === 'SIGNED' ? (
            <>
              Approved by <UserRef id={slot.signedBy} />
            </>
          ) : slot?.status === 'DECLINED' ? (
            <>
              Declined by <UserRef id={slot.signedBy} />
            </>
          ) : (
            `Nothing to decide: the change is ${CHANGE_STATUS_LABEL[change.status]?.toLowerCase() ?? change.status.toLowerCase()}.`
          )}
          {/* A step that no longer waits is cleared by hand; a live one never is. */}
          <Button
            size="small"
            color="inherit"
            disabled={execute.isPending}
            onClick={() =>
              execute.mutate({ workItemId: item.workItemId, actionId: 'DISMISS_WORK_ITEM', input: { reason: 'Step no longer open' } })
            }
            sx={{ textTransform: 'none', ml: 1.5, color: 'text.secondary' }}
          >
            Clear from queue
          </Button>
        </Typography>
      )}

      {decide.isError && (
        <Alert severity="error" variant="outlined" sx={{ mt: 1.25 }}>
          {(decide.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (decide.error as Error).message}
        </Alert>
      )}

      <SendForApprovalDialog
        open={dialog !== null}
        mode={dialog ?? 'send-back'}
        number={change.ecoNumber ?? change.number}
        title={change.title}
        lineCount={parts}
        saving={decide.isPending}
        onClose={() => setDialog(null)}
        onSend={(note) =>
          decide.mutate({ decision: dialog ?? 'send-back', note, slotId: slot?.slotId }, { onSuccess: () => setDialog(null) })
        }
      />
    </Box>
  );
};

export default ChangeApprovalCard;
